<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading…</div>
    <div v-if="loaded" class="root">

      <!-- Nav -->
      <nav class="nav">
        <img :src="darkLogoUrl" class="nav-logo" />
        <div class="nav-right">
          <span class="nav-names">{{ loName || "Loan Officer" }} <span class="nav-amp">+</span> {{ realtorName || "Realtor" }}</span>
    
        </div>
      </nav>

      <!-- Overlap layout -->
      <div class="layout">

        <!-- Left col: dark panel with hero text -->
        <div class="left-col">
          <div class="left-inner">
  
            <h1 class="headline" v-html="heroH1Html"></h1>
            <p class="body-copy">{{ heroBody }}</p>
            <div class="btns">
              <a class="btn-accent" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="btn-ghost" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
            <div class="lo-quick">
              <a v-if="loinfo && loinfo.phone_number" class="lq-link" :href="'tel:' + loinfo.phone_number">Call</a>
              <a v-if="loinfo && loinfo.email" class="lq-link" :href="'mailto:' + loinfo.email">Email</a>
              <a v-if="realtor && realtor.website_url" class="lq-link" :href="realtor.website_url" target="_blank" rel="noreferrer">Partner</a>
            </div>
          </div>
        </div>

        <!-- Right col: offset cards -->
        <div class="right-col">

          <!-- LO card — pushed up, overlaps the top -->
          <div class="float-card lo-float">
            <div class="fc-bar"></div>
            <div class="fc-row">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="fc-img" />
              <div class="fc-text">
                <span class="fc-tag">Loan Officer</span>
                <div class="fc-name">{{ loName }}</div>
                <div class="fc-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="fc-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="fc-chips">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="fc-chip">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="fc-chip">{{ loinfo.email }}</a>
            </div>
          
          </div>

          <!-- Realtor card — offset lower -->
          <div class="float-card re-float">
            <div class="fc-bar re-bar"></div>
            <div class="fc-row">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="fc-img" />
              <div class="fc-text">
                <span class="fc-tag re-tag-txt">{{ realtor?.partner_title || 'Partner' }}</span>
                <div class="fc-name">{{ realtorName }}</div>
                <div class="fc-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
                <div v-if="realtor && realtor.nmls" class="fc-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="fc-chips">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="fc-chip re-fc-chip">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="fc-chip re-fc-chip">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="fc-chip re-fc-chip" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending:        { primary: "#eb610a", accent: "#646363" },
  radiantmortgage:    { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage:     { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam:         { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage:         { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage:     { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial:  { primary: "#0b1726", accent: "#c69c39" },
};
const DBA_BASE = "https://api.goldstarfinancial.com/DBA";
function dbaLogo(brandKey, variant = "primary") { return `${DBA_BASE}/${brandKey}/${variant}.png`; }
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandOverlap",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    darkLogoUrl() { return dbaLogo(this.brandKey, "primary"); },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Clash+Display:wght@400;600;700&family=Cabinet+Grotesk:wght@300;400;500;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

.page {
  min-height: 100vh;
  background: #f1ede8;
  color: #111;
  font-family: 'DM Sans', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --hairline: rgba(0,0,0,0.09);
  --muted: #6b7280;
  --card-bg: #ffffff;
  --a-tint: color-mix(in srgb, var(--a) 10%, #fff);
  --p-tint: color-mix(in srgb, var(--p) 5%, #fff);
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* NAV */
.nav {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 48px;
  background: var(--p);
}
.nav-logo { width: 160px;  object-fit: contain;  filter: brightness(0) invert(1); opacity: 0.9; } .nav-logo { width: 160px; height: auto; object-fit: contain; }
.nav-right { display: flex; align-items: center; gap: 20px; }
.nav-names { font-size: 13px; color: rgba(255,255,255,0.55); }
.nav-amp { color: var(--a); margin: 0 4px; }
.nav-cta {
  font-size: 13px; font-weight: 600; text-decoration: none;
  background: var(--a); color: var(--p);
  padding: 9px 20px; border-radius: 6px; transition: opacity 0.2s;
}
.nav-cta:hover { opacity: 0.88; }

/* LAYOUT — full height, left dark / right light offset */
.layout {
  display: grid;
  grid-template-columns: 52% 48%;
  flex: 1;
  min-height: calc(100vh - 70px);
}

/* LEFT COL */
.left-col {
  background: var(--p);
  display: flex; align-items: center;
  padding: 60px 64px;
  position: relative;
}
/* Bottom-right accent corner */
.left-col::after {
  content: "";
  position: absolute; bottom: 0; right: 0;
  width: 60px; height: 60px;
  background: var(--a);
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
}

.eyebrow { display: block; font-size: 11px; font-weight: 500; letter-spacing: 0.22em; text-transform: uppercase; color: var(--a); margin-bottom: 16px; }
.headline {
  font-family: 'Bebas Neue', sans-serif;
  font-size: clamp(60px, 8vw, 100px);
  line-height: 0.95; letter-spacing: 0.01em;
  color: #fff; margin: 0 0 22px;
}
.headline em { color: var(--a); font-style: normal; }
.body-copy { font-size: 14px; line-height: 1.75; color: rgba(255,255,255,0.55);  margin-bottom: 28px; }
.btns { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 24px;justify-content: center; }
.btn-accent {
  font-size: 14px; font-weight: 600; text-decoration: none;
  background: var(--a); color: var(--p);
  padding: 13px 26px; border-radius: 8px; transition: opacity 0.2s;
  width:55%
}
.btn-accent:hover { opacity: 0.88; }
.btn-ghost {
  font-size: 14px; font-weight: 500; text-decoration: none;
  color: rgba(255,255,255,0.7);
  border: 1px solid rgba(255,255,255,0.2);
  padding: 13px 26px; border-radius: 8px; transition: background 0.18s;
}
.btn-ghost:hover { background: rgba(255,255,255,0.08); }
.lo-quick { display: flex; gap: 14px; }
.lq-link { font-size: 12px; text-decoration: none; color: rgba(255,255,255,0.4); border-bottom: 1px solid rgba(255,255,255,0.15); padding-bottom: 1px; transition: color 0.18s; }
.lq-link:hover { color: var(--a); }

/* RIGHT COL */
.right-col {
  background: #f1ede8;
  display: flex; flex-direction: column;
  justify-content: center;
  padding: 48px 48px 48px 32px;
  gap: 16px;
  position: relative;
}

/* FLOAT CARDS */
.float-card {
  background: var(--card-bg);
  border-radius: 18px;
  padding: 20px;
  box-shadow: 0 12px 36px rgba(0,0,0,0.10), 0 3px 10px rgba(0,0,0,0.06);
  border: 1px solid var(--hairline);
  position: relative; overflow: hidden;
  transition: transform 0.22s, box-shadow 0.22s;
}
.float-card:hover { transform: translateY(-3px); box-shadow: 0 20px 48px rgba(0,0,0,0.14); }

/* LO card: shifted left to overlap the dark panel */
.lo-float { margin-left: -52px; }
/* RE card: normal position */
.re-float { margin-left: 0; }

.fc-bar {
  position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, var(--p), color-mix(in srgb, var(--p) 50%, var(--a)));
}
.re-bar { background: linear-gradient(90deg, var(--a), color-mix(in srgb, var(--a) 50%, var(--p))); }

.fc-row { display: flex; gap: 14px; align-items: center; margin: 8px 0 14px; }
.fc-img { width: 58px; height: 58px; border-radius: 12px; object-fit: cover; }
.fc-tag { font-size: 9px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: color-mix(in srgb, var(--p) 55%, var(--muted)); display: block; margin-bottom: 4px; }
.re-tag-txt { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.fc-name { font-size: 18px; font-weight: 700; color: var(--p); line-height: 1.2; }
.fc-role { font-size: 12px; color: var(--muted); margin-top: 2px; }
.fc-nmls { font-size: 11px; color: color-mix(in srgb, var(--muted) 65%, transparent); margin-top: 2px; }
.fc-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.fc-chip {
  font-size: 11px; font-weight: 500; text-decoration: none;
  background: var(--p-tint); color: var(--p);
  padding: 5px 12px; border-radius: 50px;
  border: 1px solid var(--hairline); transition: background 0.18s;
}
.fc-chip:hover { background: color-mix(in srgb, var(--p) 10%, #fff); }
.re-fc-chip { background: var(--a-tint); color: color-mix(in srgb, var(--a) 85%, var(--p)); border-color: color-mix(in srgb, var(--a) 18%, transparent); }
.re-fc-chip:hover { background: color-mix(in srgb, var(--a) 18%, #fff); }
.fc-btn {
  display: block; text-align: center; text-decoration: none;
  background: var(--p); color: #fff;
  font-size: 13px; font-weight: 600; padding: 11px;
  border-radius: 10px; transition: opacity 0.18s;
}
.fc-btn:hover { opacity: 0.85; }

@media (max-width: 820px) {
  .layout { grid-template-columns: 1fr; }
  .left-col { padding: 44px 24px; }
  .right-col { padding: 32px 24px; }
  .lo-float { margin-left: 0; }
  .nav { padding: 16px 24px; }
}
</style>